import React, { useEffect, useState } from "react";
import { Constants } from "@eagerdog/constants";
import { IDog } from "@eagerdog/interfaces";

import { helperService } from "src/services/helper.service";
import { apiService } from "src/services/api.service";
import { toast } from "src/components/Toast/ToastManager";
import Button from "src/components/Button/Button";
import Dropdown, { IOption } from "src/components/Dropdown/Dropdown";
import TextArea from "src/components/TextArea/TextArea";
import { notifyPostCreated, showPostError } from "../postFeedback";

import styles from "./MomentComposer.module.scss";

interface IProps {
    mediaType: "image" | "video";
    onClose: () => void;
    onSuccess: () => void;
    /** When set (e.g. from a dog's profile), the dog is fixed and the dog picker is hidden. */
    dog?: IDog;
}

const MomentComposer: React.FC<IProps> = ({ mediaType, onClose, onSuccess, dog: fixedDog }) => {
    const user = helperService.getUser();

    const [dogs, setDogs] = useState<IDog[]>(fixedDog ? [fixedDog] : []);
    const [dogId, setDogId] = useState<string>(fixedDog?._id || "");
    const [file, setFile] = useState<File | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string>("");
    const [caption, setCaption] = useState<string>("");
    const [dogsLoaded, setDogsLoaded] = useState<boolean>(!!fixedDog);
    const [submitting, setSubmitting] = useState<boolean>(false);

    useEffect(() => {
        if (!user._id || fixedDog) return;
        apiService.getUserDogs(user._id).then((data) => {
            setDogs(data);
            if (data.length > 0) setDogId(data[0]._id);
        }).catch(() => {}).finally(() => setDogsLoaded(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user._id]);

    useEffect(() => {
        if (!file) {
            setPreviewUrl("");
            return;
        }
        const url = URL.createObjectURL(file);
        setPreviewUrl(url);
        return () => URL.revokeObjectURL(url);
    }, [file]);

    const onSelectFile = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFile(e.target.files?.[0] || null);
    };

    const selectedDog = dogs.find((d) => d._id === dogId);
    const dogOptions: IOption[] = dogs.map((dog) => ({ id: dog._id, value: dog.call_name }));
    const dogValue: IOption = dogOptions.find((option) => option.id === dogId) || { id: "", value: "" };

    const handleSubmit = async () => {
        if (!dogId) {
            toast.show({
                title: mediaType === "video" ? "Add a video" : "Add a photo",
                content: "Choose a dog to post.",
                duration: 6000,
                type: "fail",
            });
            return;
        }

        if (!file) {
            toast.show({
                title: mediaType === "video" ? "Add a video" : "Add a photo",
                content: `Choose a ${mediaType} to share first.`,
                duration: 6000,
                type: "fail",
            });
            return;
        }

        setSubmitting(true);
        try {
            const uploaded: any = await apiService.uploadFile(file);
            const url = typeof uploaded === "string" ? uploaded : uploaded?.value;
            if (typeof url !== "string" || !url) {
                throw new Error("Upload did not return a file URL");
            }
            const created = await apiService.createPost({
                post_type: mediaType === Constants.post_media_type.video ? Constants.post_type.video : Constants.post_type.photo,
                media: [{ type: mediaType, url }],
                dog_id: dogId,
                body: caption.trim() || undefined,
            });
            notifyPostCreated(created);
            onSuccess();
            onClose();
        } catch (err) {
            showPostError(err, {
                title: "Share failed",
                content: "Unable to share right now. Please try again.",
            });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className={styles.MomentComposer}>
            <div className={styles.title}>{mediaType === "video" ? "Add a video" : "Add a photo"}</div>

            {selectedDog && (
                <div className={styles.posterRow}>
                    <div className={styles.avatar}>
                        {selectedDog.image_url
                            ? <img src={selectedDog.image_url} alt={selectedDog.call_name} />
                            : <span>{selectedDog.call_name.charAt(0).toUpperCase()}</span>}
                    </div>
                    <div className={styles.posterText}>
                        <div className={styles.posterName}>{selectedDog.call_name}</div>
                        <div className={styles.posterSub}>{`Posting to ${selectedDog.call_name}’s followers`}</div>
                    </div>
                </div>
            )}

            {dogsLoaded && dogs.length === 0 && (
                <div className={styles.posterSub}>Add a dog before you can post.</div>
            )}

            {!fixedDog && dogs.length > 1 && (
                <Dropdown
                    label="Dog"
                    placeholder="Choose a dog"
                    options={dogOptions}
                    value={dogValue}
                    onChange={(_e, option) => setDogId(option.id)}
                />
            )}

            <label className={styles.uploadArea}>
                <input type="file" accept={`${mediaType}/*`} onChange={onSelectFile} />
                {previewUrl ? (
                    mediaType === "video"
                        ? <video src={previewUrl} controls className={styles.preview} />
                        : <img src={previewUrl} alt="" className={styles.preview} />
                ) : (
                    <>
                        <div className={styles.uploadIcon}>↑</div>
                        <div className={styles.uploadTitle}>
                            {mediaType === "video" ? "Drag a video here" : "Drag photos here"}
                        </div>
                        <div className={styles.uploadHint}>
                            {mediaType === "video" ? "MP4 or MOV up to 500 MB · 3 minutes max" : "JPG or PNG up to 20 MB"}
                        </div>
                        <span className={styles.chooseFileBtn}>Choose file</span>
                    </>
                )}
            </label>

            <TextArea
                label="Caption"
                placeholder="Say something about this…"
                value={caption}
                onChange={(e) => setCaption(e.target.value.slice(0, 2000))}
                className={styles.captionField}
            />

            <div className={styles.footer}>
                <div className={styles.footerActions}>
                    <Button variant="secondary" onClick={onClose} disabled={submitting}>Cancel</Button>
                    <Button onClick={handleSubmit} disabled={!selectedDog} isLoading={submitting}>Post</Button>
                </div>
            </div>
        </div>
    );
};

export default MomentComposer;
